import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";
import { Play, Clock } from "lucide-react";
import GameArt from "./GameArt";

const GameCard = ({ game, t, lang, index }) => {
  const ref = useRef(null);
  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 120, damping: 14 });
  const sry = useSpring(ry, { stiffness: 120, damping: 14 });

  const onMouseMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 7);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 7);
  };

  const onMouseLeave = () => {
    rx.set(0);
    ry.set(0);
  };

  const isLive = game.status === "live";

  return (
    <motion.article
      ref={ref}
      data-testid={`game-card-${game.id}`}
      initial={{ opacity: 0, y: 40, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: index * 0.15, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
      style={{ rotateX: srx, rotateY: sry, transformPerspective: 900 }}
      className="glass rounded-3xl overflow-hidden group hover:border-[#00F0FF]/30 transition-colors duration-500"
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <div className="absolute inset-0 transition-transform duration-700 ease-out group-hover:scale-110">
          <GameArt game={game} />
        </div>
        <span
          data-testid={`game-badge-${game.id}`}
          className={`absolute top-4 left-4 text-[10px] font-bold tracking-[0.2em] uppercase rounded-full px-3 py-1.5 backdrop-blur-md ${
            isLive ? "bg-[#00F0FF]/15 text-[#00F0FF]" : "bg-white/10 text-white/60"
          }`}
        >
          {isLive ? t.catalog.liveBadge : t.catalog.soonBadge}
        </span>
      </div>

      <div className="p-7 md:p-8">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 mb-2">
          {game.tagline[lang]}
        </p>
        <h3 className="font-display font-bold text-2xl md:text-3xl tracking-tight mb-3">
          {game.name}
        </h3>
        <p className="text-sm md:text-base leading-relaxed text-white/60 mb-7">
          {game.description[lang]}
        </p>

        <span
          data-testid={`game-cta-${game.id}`}
          className={
            isLive
              ? "btn-magic inline-flex items-center gap-2 rounded-full px-6 py-3 font-display font-bold text-xs tracking-wider uppercase text-white"
              : "inline-flex items-center gap-2 rounded-full px-6 py-3 font-display font-bold text-xs tracking-wider uppercase text-white/40 border border-white/15"
          }
        >
          {isLive ? (
            <Play className="w-3.5 h-3.5 fill-current" />
          ) : (
            <Clock className="w-3.5 h-3.5" />
          )}
          {isLive ? t.catalog.discover : t.catalog.soon}
        </span>
      </div>
    </motion.article>
  );
};

export default GameCard;
